import { FC, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { colors, fontStyles, spacing } from '../../styles';
import { getSymbolKeyEvents, PortfolioKeyEvent } from '../../apis/backend';
import SkeletonBox from '../SkeletonBox';
import EventCard from './KeyEvents/EventCard';

interface Props {
  symbol: string;
}

const MAX_VISIBLE_SYMBOLS = 3;
const EVENTS_PER_PAGE = 20;

const AllKeyEventsPage: FC<Props> = ({ symbol }) => {
  const { i18n } = useTranslation();
  const history = useHistory();
  const [allEvents, setAllEvents] = useState<PortfolioKeyEvent[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Reset state when symbol changes
  useEffect(() => {
    setAllEvents([]);
    setHasMore(true);
    setTotalCount(0);
  }, [symbol]);

  const { isLoading: isInitialLoading, error } = useQuery(
    ['symbolKeyEvents', symbol, 'paginated', i18n.language],
    () => getSymbolKeyEvents(symbol, EVENTS_PER_PAGE, 0, true),
    {
      enabled: !!symbol,
      onSuccess: (data) => {
        setAllEvents(data.events);
        setHasMore(data.has_more || false);
        setTotalCount(data.total_count || 0);
      },
    },
  );

  const handleEventClick = (event: PortfolioKeyEvent) => {
    if (event.route && event.route !== '') {
      history.push(event.route);
    }
  };

  const handleEventKeyDown = (keyboardEvent: React.KeyboardEvent, event: PortfolioKeyEvent) => {
    if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
      keyboardEvent.preventDefault();
      handleEventClick(event);
    }
  };

  const handleLoadMore = async () => {
    if (!hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const nextOffset = allEvents.length;
      const moreData = await getSymbolKeyEvents(symbol, EVENTS_PER_PAGE, nextOffset, true);

      setAllEvents((prev) => [...prev, ...moreData.events]);
      setHasMore(moreData.has_more || false);
    } catch (error) {
      console.error('Error loading more events:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const renderEmptyState = () => (
    <div
      css={css`
        text-align: center;
        padding: ${spacing.regular};
        color: ${colors.silver};
        ${fontStyles.dog};
      `}
    >
      No key events found for {symbol}
    </div>
  );

  const renderErrorState = () => (
    <div
      css={css`
        text-align: center;
        padding: ${spacing.regular};
        color: ${colors.berry};
        ${fontStyles.dog};
      `}
    >
      Failed to load key events. Please try again later.
    </div>
  );

  const renderLoadingState = () => (
    <div
      css={css`
        margin-top: ${spacing.deci};
      `}
    >
      <SkeletonBox width="100%" />
      <SkeletonBox width="100%" />
      <SkeletonBox width="100%" />
      <SkeletonBox width="100%" />
      <SkeletonBox width="100%" />
    </div>
  );

  const renderLoadMoreButton = () => {
    if (!hasMore) return null;

    return (
      <button
        css={css`
          width: 100%;
          margin-top: ${spacing.deci};
          padding: ${spacing.deci};
          background-color: transparent;
          border: 1px solid ${colors.petrolBlue};
          border-radius: 8px;
          color: ${colors.petrolBlue};
          cursor: pointer;
          transition: all 0.2s ease;
          ${fontStyles.cat};
          display: flex;
          align-items: center;
          justify-content: center;
          gap: ${spacing.milli};

          &:hover {
            background-color: ${colors.petrolBlue}08;
          }

          &:focus {
            outline: 2px solid ${colors.petrolBlue};
            outline-offset: 2px;
          }

          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          html[data-theme='dark'] & {
            border-color: ${colors.skyBlue};
            color: ${colors.skyBlue};

            &:hover {
              background-color: ${colors.skyBlue}08;
            }

            &:focus {
              outline-color: ${colors.skyBlue};
            }
          }
        `}
        onClick={handleLoadMore}
        disabled={isLoadingMore}
      >
        {isLoadingMore ? 'Loading...' : `Load More Events (${totalCount - allEvents.length} remaining)`}
      </button>
    );
  };

  return (
    <div>
      <h2 css={styles.heading}>Key Events</h2>
      {error ? (
        renderErrorState()
      ) : isInitialLoading ? (
        renderLoadingState()
      ) : allEvents.length > 0 ? (
        <div
          css={css`
            margin-top: ${spacing.deci};
          `}
        >
          {allEvents.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              onEventClick={handleEventClick}
              onEventKeyDown={handleEventKeyDown}
              maxVisibleSymbols={MAX_VISIBLE_SYMBOLS}
            />
          ))}
          {renderLoadMoreButton()}
          {isLoadingMore && (
            <div
              css={css`
                margin-top: ${spacing.deci};
              `}
            >
              <SkeletonBox width="100%" />
              <SkeletonBox width="100%" />
              <SkeletonBox width="100%" />
            </div>
          )}
        </div>
      ) : (
        renderEmptyState()
      )}
    </div>
  );
};

const styles = {
  heading: css`
    margin: 0;
    padding: ${spacing.deci} 0 ${spacing.centi} 0;
    ${fontStyles.shark};
    color: var(--primary-text);
  `,
};

export default AllKeyEventsPage;
